/**
 * Sichtbare Release-/Build-Version im UI (Sidebar-Footer + Login).
 *
 * Quelle ist die beim UI-Build generierte `public/dist/build-info.json`
 * (scripts/build-ui.ts): version aus package.json, commit aus COMMIT_SHA-Env
 * oder `git rev-parse --short HEAD`, builtAt aus BUILD_DATE-Env oder Build-Zeit.
 * Fehlt die Datei (z.B. `ui:dev`-Watch ohne Build) → Fallback
 * "dev · unknown · built unknown". Keine Secrets, rein informativ.
 */

import { signal } from "@preact/signals";

export interface BuildInfo {
  version: string;
  commit: string;
  builtAt: string;
}

export const BUILD_INFO_URL = "/dist/build-info.json";
export const BUILD_INFO_FALLBACK = "dev · unknown · built unknown";

/** Rohes JSON defensiv normalisieren; komplett leere/kaputte Daten → null. */
export function normalizeBuildInfo(raw: unknown): BuildInfo | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const s = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
  const version = s(r.version);
  const commit = s(r.commit);
  const builtAt = s(r.builtAt);
  if (!version && !commit && !builtAt) return null;
  return { version: version || "dev", commit: commit || "unknown", builtAt };
}

/** ISO-Zeitstempel → "YYYY-MM-DD HH:mm" (lokale Zeit); ungültig → "unknown". */
export function formatBuiltAt(iso: string): string {
  if (!iso) return "unknown";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "unknown";
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Anzeige-String: "v0.1.0 · abc1234 · built 2026-07-05 14:12" (mit Fallbacks). */
export function formatBuildInfo(info: BuildInfo | null): string {
  if (!info) return BUILD_INFO_FALLBACK;
  const v = info.version === "dev" || info.version.startsWith("v") ? info.version : `v${info.version}`;
  return `${v} · ${info.commit || "unknown"} · built ${formatBuiltAt(info.builtAt)}`;
}

export const buildInfo = signal<BuildInfo | null>(null);

let requested = false;

/** Einmalig (idempotent) die Build-Info laden; Fehler → Fallback bleibt. */
export function loadBuildInfo(): void {
  if (requested || typeof fetch === "undefined") return;
  requested = true;
  fetch(BUILD_INFO_URL)
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => { buildInfo.value = normalizeBuildInfo(j); })
    .catch(() => { /* Datei fehlt (Dev-Watch) → Fallback-Anzeige */ });
}
